import { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  Legend,
} from "recharts";
import { EmptyScreen } from "../../../misc";
import { CustomTooltip } from "@/components/graph/misc";
import { CustomXAxisTick, CustomYAxisTick, roundToTwoDecimalPlaces } from "./utils";
import styles from "../../../styles.module.scss";

interface OverloadTimeGraphProps {
  data: Array<{
    date: string;
    avg_queued_overload_time: number;
    avg_queued_overload_time_ui: string;
    p90_queued_overload_time: number;
    p90_queued_overload_time_ui: string;
  }>;
}

const OverloadTimeGraph = ({ data }: OverloadTimeGraphProps) => {
  const [overloadTimeFilter, setOverloadTimeFilter] = useState("all");

  const handleLegendClick = (data: { value?: string }) => {
    const datakey = data?.value;

    switch (datakey) {
      case "Avg Queuing Time":
        if (overloadTimeFilter === "overload_time") {
          setOverloadTimeFilter("all");
        } else {
          setOverloadTimeFilter("overload_time");
        }
        break;
      case "P90 Queuing Time":
        if (overloadTimeFilter === "p90_overload_time") {
          setOverloadTimeFilter("all");
        } else {
          setOverloadTimeFilter("p90_overload_time");
        }
        break;
      default:
        setOverloadTimeFilter("all");
    }
  };

  const stackedData = useMemo(() => {
    if (data.length === 0) return [];
    return data.map(entry => ({
      time: entry.date,
      overload_time: roundToTwoDecimalPlaces(entry.avg_queued_overload_time),
      overload_time_ui: entry.avg_queued_overload_time_ui,
      p90_overload_time: roundToTwoDecimalPlaces(entry.p90_queued_overload_time),
      p90_overload_time_ui: entry.p90_queued_overload_time_ui,
      p90_overload_time_stack: roundToTwoDecimalPlaces(
        entry.p90_queued_overload_time - entry.avg_queued_overload_time
      ),
    }));
  }, [data]);

  if (stackedData && stackedData.length === 0)
    return (
      <EmptyScreen
        title="No Queuing time data found"
        subTitle="No Queuing time data found for the selected period. You will see the data once queries are executed."
      />
    );

  return (
    <ResponsiveContainer minHeight={400} className="mb-4">
      <AreaChart
        data={stackedData}
        margin={{
          top: 50,
          right: 30,
          left: 50,
          bottom: 40,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="time" tick={<CustomXAxisTick />} />
        <YAxis
          label={{
            value: "Query Queuing Time",
            fill: "black",
            angle: 0,
            position: "insideTopLeft",
            offset: -30,
          }}
          tick={<CustomYAxisTick />}
        />
        <Tooltip
          content={
            <CustomTooltip
              nameValue={[
                { name: "Date", value: "time", color: "gray" },
                {
                  name: "Avg Queuing Time",
                  value: "overload_time_ui",
                  color: "#247EFE",
                },
                {
                  name: "P90 Queuing Time",
                  value: "p90_overload_time_ui",
                  color: "#01CD8C",
                },
              ]}
            />
          }
        />
        <Area
          type="linear"
          name="Avg Queuing Time"
          dataKey={
            overloadTimeFilter === "all" || overloadTimeFilter === "overload_time"
              ? "overload_time"
              : "overload_time_hidden"
          }
          stroke="#53B1FD"
          fill="#53B1FD"
          dot={{
            fill: "#C0D7F9",
            strokeWidth: "1px",
            stroke: "#247EFE",
          }}
          stackId={1}
          aria-label="Average queuing time"
        />
        <Area
          type="linear"
          name="P90 Queuing Time"
          dataKey={
            overloadTimeFilter === "all" || overloadTimeFilter === "p90_overload_time"
              ? "p90_overload_time"
              : "p90_overload_time_hidden"
          }
          stroke="#52c7a2"
          fill="#52c7a2"
          dot={{
            fill: "#52c7a2",
            strokeWidth: "1px",
            stroke: "#52c7a2",
          }}
          stackId={1}
          aria-label="P90 queuing time"
        />
        <Legend
          align="center"
          verticalAlign="bottom"
          wrapperStyle={{ bottom: -20 }}
          onClick={handleLegendClick}
          formatter={(_value: string) => <span className={styles.legend}>{_value}</span>}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default OverloadTimeGraph;